// SocialCommunityPanel.js - Panneau de communauté et networking social
import React, { useState, useEffect } from 'react';
import { 
  Users, MessageCircle, Calendar, UserPlus, Heart, Star, 
  MapPin, Clock, Award, TrendingUp, Send, Search, Filter,
  ThumbsUp, Share2, Bookmark, Eye, MoreHorizontal, Bell,
  Video, Coffee, Briefcase, Globe, Target, CheckCircle,
  ArrowRight, ExternalLink, Plus, Zap, Crown, Medal,
  MessageSquare, UserCheck, Handshake, Lightbulb, Flame
} from 'lucide-react';

const SocialCommunityPanel = ({ 
  socialData, 
  userObjective, 
  actionPlan, 
  userProgress,
  onSocialInteraction,
  onJoinGroup,
  onJoinEvent,
  onStartCollaboration 
}) => {
  const [activeTab, setActiveTab] = useState('community'); // community | discussions | events | collaborations
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showProfile, setShowProfile] = useState(null);
  const [newMessage, setNewMessage] = useState('');

  // États pour les interactions en temps réel
  const [onlineUsers, setOnlineUsers] = useState(socialData.users || []);
  const [recentActivity, setRecentActivity] = useState([]);
  const [notifications, setNotifications] = useState([]);

  // Simulation d'activité en temps réel
  useEffect(() => {
    const mockActivity = [
      {
        id: 1,
        type: 'achievement',
        user: 'Marie Laurent',
        action: 'a débloqué l\'accomplissement "Expert stratège"',
        timestamp: '2 min',
        icon: Award
      },
      {
        id: 2,
        type: 'collaboration',
        user: 'Thomas Chen',
        action: 'recherche un partenaire pour une étude de marché',
        timestamp: '5 min',
        icon: Handshake
      },
      {
        id: 3,
        type: 'discussion',
        user: 'Sophie Martin',
        action: 'a posté dans "Stratégies marketing 2025"',
        timestamp: '12 min',
        icon: MessageCircle
      },
      {
        id: 4,
        type: 'event',
        user: 'Communauté',
        action: 'Nouveau webinar "IA et productivité" programmé',
        timestamp: '15 min',
        icon: Calendar
      }
    ];
    setRecentActivity(mockActivity);

    // Simulation de notifications
    const mockNotifications = [
      {
        id: 1,
        type: 'mention',
        message: 'Marie Laurent vous a mentionné dans une discussion',
        timestamp: '1 min',
        read: false
      },
      {
        id: 2,
        type: 'collaboration',
        message: 'Nouvelle demande de collaboration reçue',
        timestamp: '5 min',
        read: false
      }
    ];
    setNotifications(mockNotifications);
  }, []);

  // Filtrage des utilisateurs
  const filteredUsers = onlineUsers.filter(user => {
    const matchesSearch = searchQuery === '' || 
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.expertise.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = selectedFilter === 'all' ||
      (selectedFilter === 'online' && user.status === 'online') ||
      (selectedFilter === 'mentors' && user.availability?.includes('Mentor')) ||
      (selectedFilter === 'collaborators' && user.availability?.includes('collaboration'));
    
    return matchesSearch && matchesFilter;
  });

  // Recommandations basées sur l'objectif utilisateur
  const getRecommendations = () => {
    return {
      users: onlineUsers.filter(user => 
        user.commonInterests?.some(interest => 
          userObjective?.toLowerCase().includes(interest.toLowerCase())
        )
      ).slice(0, 3),
      groups: socialData.groups?.filter(group =>
        userObjective?.toLowerCase().includes(group.category.toLowerCase())
      ).slice(0, 2) || [],
      discussions: socialData.discussions?.filter(disc =>
        disc.title.toLowerCase().includes('stratég') || 
        disc.title.toLowerCase().includes('analys')
      ).slice(0, 2) || []
    };
  };

  const recommendations = getRecommendations();

  // Composant Profil utilisateur
  const UserProfile = ({ user, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl max-h-96 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Profil utilisateur</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <MoreHorizontal className="h-5 w-5" />
          </button>
        </div>
        
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 relative">
            <span className="text-2xl">{user.avatar}</span>
            {user.status === 'online' && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-400 rounded-full border-4 border-white"></div>
            )}
          </div>
          <h4 className="font-semibold text-lg text-gray-800">{user.name}</h4>
          <p className="text-blue-600 font-medium">{user.expertise}</p>
          {user.location && (
            <p className="text-sm text-gray-600 flex items-center justify-center mt-1">
              <MapPin className="h-3 w-3 mr-1" />
              {user.location}
            </p>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Points</span>
              <div className="flex items-center">
                <Star className="h-4 w-4 text-yellow-500 mr-1" />
                <span className="font-semibold">{user.points}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Statut</span>
              <span className="text-sm font-medium text-green-600">{user.availability}</span>
            </div>
          </div>

          {user.commonInterests && (
            <div>
              <h5 className="font-medium text-gray-800 mb-2">Centres d'intérêt communs</h5>
              <div className="flex flex-wrap gap-1">
                {user.commonInterests.map((interest, i) => (
                  <span key={i} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex space-x-2 pt-4">
            <button
              onClick={() => onSocialInteraction(user.id, 'message')}
              className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center justify-center"
            >
              <MessageCircle className="h-4 w-4 mr-1" />
              Message
            </button>
            <button
              onClick={() => onSocialInteraction(user.id, 'follow')}
              className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center justify-center"
            >
              <UserPlus className="h-4 w-4 mr-1" />
              Suivre
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header avec notifications et recherche */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b border-blue-100 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-blue-800">Communauté Aladinn</h2>
            <p className="text-sm text-blue-600">Connectez-vous, collaborez et grandissez ensemble</p>
          </div>
          
          {/* Notifications */}
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Bell className="h-5 w-5 text-blue-600" />
              {notifications.filter(n => !n.read).length > 0 && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-xs text-white font-bold">
                    {notifications.filter(n => !n.read).length}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2 bg-green-50 px-3 py-1 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-green-800">
                {onlineUsers.filter(u => u.status === 'online').length} en ligne
              </span>
            </div>
          </div>
        </div>

        {/* Navigation et recherche */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          {/* Onglets */}
          <div className="flex space-x-1 bg-white rounded-lg p-1">
            {[
              { id: 'community', label: 'Communauté', icon: Users },
              { id: 'discussions', label: 'Discussions', icon: MessageCircle },
              { id: 'events', label: 'Événements', icon: Calendar },
              { id: 'collaborations', label: 'Collaborations', icon: Handshake }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Recherche */}
          <div className="flex items-center space-x-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">Tous</option>
              <option value="online">En ligne</option>
              <option value="mentors">Mentors</option>
              <option value="collaborators">Collaborateurs</option>
            </select>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Recommandations personnalisées */}
        {activeTab === 'community' && recommendations.users.length > 0 && (
          <div className="mb-6 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
            <h3 className="font-semibold text-purple-800 mb-3 flex items-center">
              <Lightbulb className="h-5 w-5 mr-2" />
              Recommandations pour vous
            </h3>
            <p className="text-sm text-purple-600 mb-3">
              Basées sur votre objectif : "{userObjective?.slice(0, 60)}..."
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {recommendations.users.map(user => (
                <div key={user.id} className="bg-white p-3 rounded-lg border border-purple-100">
                  <div className="flex items-center mb-2">
                    <span className="text-lg mr-2">{user.avatar}</span>
                    <div className="flex-1">
                      <h5 className="font-medium text-sm text-purple-800">{user.name}</h5>
                      <p className="text-xs text-purple-600">{user.expertise}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => onSocialInteraction(user.id, 'connect')}
                    className="w-full px-2 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700"
                  >
                    Se connecter
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activité récente */}
        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            Activité récente
          </h3>
          <div className="space-y-3">
            {recentActivity.map(activity => {
              const IconComponent = activity.icon;
              return (
                <div key={activity.id} className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded">
                  <IconComponent className="h-4 w-4 text-blue-600 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-800">
                      <span className="font-medium">{activity.user}</span> {activity.action}
                    </p>
                    <p className="text-xs text-gray-500">{activity.timestamp}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Contenu spécifique à chaque onglet */}
        {activeTab === 'community' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Utilisateurs en ligne */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-4">
                Membres actifs ({filteredUsers.length})
              </h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredUsers.map(user => (
                  <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3 relative">
                        <span className="text-sm">{user.avatar}</span>
                        {user.status === 'online' && (
                          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h5 className="font-medium text-sm text-gray-800">{user.name}</h5>
                        <p className="text-xs text-gray-600">{user.expertise}</p>
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <Star className="h-3 w-3 mr-1" />
                          {user.points} points
                          {user.location && (
                            <>
                              <span className="mx-1">•</span>
                              <MapPin className="h-3 w-3 mr-1" />
                              {user.location}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => setShowProfile(user)}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onSocialInteraction(user.id, 'message')}
                        className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                      >
                        <MessageCircle className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Groupes et communautés */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-4">
                Groupes et communautés
              </h3>
              <div className="space-y-3">
                {socialData.groups?.map(group => (
                  <div key={group.id} className="p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium text-gray-800">{group.name}</h5>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        {group.category}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{group.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-gray-500">
                        <Users className="h-3 w-3 mr-1" />
                        {group.members} membres
                      </div>
                      <button
                        onClick={() => onJoinGroup(group.id)}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                      >
                        Rejoindre
                      </button>
                    </div>
                  </div>
                )) || (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Aucun groupe disponible
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'discussions' && (
          <div className="space-y-4">
            {socialData.discussions?.map(discussion => (
              <div key={discussion.id} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h4 className="font-semibold text-gray-800">{discussion.title}</h4>
                      {discussion.trending && (
                        <Flame className="h-4 w-4 text-orange-500 ml-2" />
                      )}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <span>Par {discussion.author}</span>
                      <span className="mx-2">•</span>
                      <MessageSquare className="h-4 w-4 mr-1" />
                      <span>{discussion.replies} réponses</span>
                      <span className="mx-2">•</span>
                      <Clock className="h-4 w-4 mr-1" />
                      <span>{discussion.lastActivity}</span>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    discussion.category === 'Stratégie' ? 'bg-purple-100 text-purple-800' :
                    discussion.category === 'Outils' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {discussion.category}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="flex items-center space-x-1 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded">
                    <ArrowRight className="h-4 w-4" />
                    <span>Participer</span>
                  </button>
                  <button className="p-1 text-gray-400 hover:text-red-600">
                    <Heart className="h-4 w-4" />
                  </button>
                  <button className="p-1 text-gray-400 hover:text-yellow-600">
                    <Bookmark className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )) || (
              <div className="text-center py-8 text-gray-500">
                <MessageCircle className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p>Aucune discussion en cours</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'events' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {socialData.events?.map(event => (
              <div key={event.id} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800 mb-1">{event.title}</h4>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{event.date} à {event.time}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="h-4 w-4 mr-1" />
                      <span>{event.attendees} participants</span>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    event.type === 'Webinar' ? 'bg-blue-100 text-blue-800' :
                    event.type === 'Networking' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {event.type}
                  </span>
                </div>
                <button
                  onClick={() => onJoinEvent(event.id)}
                  className="w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center justify-center"
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  Participer
                </button>
              </div>
            )) || (
              <div className="col-span-2 text-center py-8 text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p>Aucun événement programmé</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'collaborations' && (
          <div className="space-y-4">
            {socialData.collaborations?.map(collab => (
              <div key={collab.id} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800 mb-1">{collab.title}</h4>
                    <p className="text-sm text-gray-600 mb-2">Proposé par {collab.author}</p>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <Target className="h-4 w-4 mr-1" />
                      <span>Échéance : {collab.deadline}</span>
                    </div>
                    {collab.skills && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {collab.skills.map((skill, i) => (
                          <span key={i} className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onStartCollaboration(collab.id)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm flex items-center"
                  >
                    <Handshake className="h-4 w-4 mr-1" />
                    Collaborer
                  </button>
                  <button className="px-3 py-2 text-gray-600 hover:text-gray-800 text-sm border border-gray-300 rounded-lg">
                    En savoir plus
                  </button>
                </div>
              </div>
            )) || (
              <div className="text-center py-8 text-gray-500">
                <Handshake className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p>Aucune collaboration disponible</p>
                <button className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm">
                  Proposer une collaboration
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Zone de chat communautaire flottante */}
      <div className="bg-white border-t border-gray-200 p-4 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Partager avec la communauté..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 pr-12"
            />
            <button
              onClick={() => {
                if (newMessage.trim()) {
                  console.log('Message envoyé:', newMessage);
                  setNewMessage('');
                }
              }}
              disabled={!newMessage.trim()}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-blue-600 hover:text-blue-800 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 text-gray-400 hover:text-blue-600 border border-gray-300 rounded-lg">
              <Video className="h-4 w-4" />
            </button>
            <button className="p-2 text-gray-400 hover:text-green-600 border border-gray-300 rounded-lg">
              <Coffee className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Modal profil utilisateur */}
      {showProfile && (
        <UserProfile 
          user={showProfile} 
          onClose={() => setShowProfile(null)} 
        />
      )}

      {/* Barre latérale d'aide contextuelle */}
      <div className="fixed bottom-4 right-4 z-40">
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 rounded-lg shadow-lg max-w-xs">
          <h4 className="font-semibold text-sm mb-1">💡 Conseil communautaire</h4>
          <p className="text-xs">
            Connectez-vous avec des experts ayant des objectifs similaires pour accélérer votre progression !
          </p>
          <button className="mt-2 text-xs underline hover:no-underline">
            En savoir plus
          </button>
        </div>
      </div>

      {/* Indicateurs de performance sociale */}
      {userProgress && (
        <div className="fixed top-20 right-4 z-30 bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
          <h4 className="font-medium text-sm text-gray-800 mb-2">Votre impact social</h4>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-600">Connexions</span>
              <span className="font-semibold">12</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Collaborations</span>
              <span className="font-semibold">3</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Aide apportée</span>
              <span className="font-semibold">8</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Points sociaux</span>
              <span className="font-semibold text-purple-600">+{Math.round(userProgress.totalPoints * 0.15)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialCommunityPanel;